import dayjs from 'dayjs';

import { getCompanyAreaLabel } from 'src/utils/mappers/eventForm/area.mapper';
import {
	NO_CHANGE_STATUS_OPTION,
	STATUS_REASON_OPTIONS,
} from 'src/utils/mappers/companyStatus.mapper';
import { mapWorthToFormValues } from 'src/utils/mappers/worth/form.mapper';
import { findJobOption } from 'src/utils/mappers/eventForm/contact.mapper';

const NUMBER_KIND_OPTIONS = {
	WORK: { label: 'Рабочий', value: 'work' },
	PERSONAL: { label: 'Личный', value: 'personal' },
};

const parseJson = (value) => {
	if (!value) return null;
	if (typeof value === 'string') {
		try {
			return JSON.parse(value);
		} catch {
			return null;
		}
	}

	return value;
};

const normalizeEnum = (value) => {
	if (!value) return '';

	if (typeof value === 'object' && value.value) {
		return String(value.value).toLowerCase();
	}

	return String(value).toLowerCase();
};

const getObjectionTypeName = (objectionTypeById, typeId) => {
	if (!typeId) return 'Возражение';

	return objectionTypeById?.[typeId]?.name ?? 'Возражение';
};

const mapContactNumbers = (numbers = []) =>
	numbers
		.map((number) => {
			if (typeof number === 'string') {
				return {
					type: NUMBER_KIND_OPTIONS.WORK,
					text: number,
				};
			}

			const kind = normalizeEnum(number.kind).toUpperCase();

			return {
				type: NUMBER_KIND_OPTIONS[kind] || NUMBER_KIND_OPTIONS.WORK,
				text: number.number ?? number.text ?? '',
			};
		})
		.filter(({ text }) => text);

const resolveActivityPayload = (activity) => {
	const version = activity?.lastVersion ?? {};
	const items = (version.items ?? []).map((item) => ({
		...item,
		entityType: normalizeEnum(item.entityType),
		role: normalizeEnum(item.role),
		operationType: normalizeEnum(item.operationType),
		oldValues: parseJson(item.oldValues),
		newValues: parseJson(item.newValues),
		payloadJson: parseJson(item.payloadJson),
	}));

	let payload = parseJson(version.payloadJson) ?? {};

	const hasActions = Boolean(
		payload.companyStatusId ||
			payload.statusPostponedUntil ||
			payload.objections?.length ||
			payload.personalContacts?.length ||
			payload.generalNumbers?.length ||
			payload.contracts?.length ||
			payload.documents?.length ||
			payload.companyParams ||
			payload.meetings?.length ||
			payload.messagings?.length ||
			payload.calls?.attach?.length ||
			payload.worth ||
			payload.companyGroupLinks?.link?.length ||
			payload.companyGroupLinks?.unlink?.length,
	);

	if (!hasActions && items.length) {
		payload = { ...payload, ...buildPayloadFromItems(items) };
	} else if (items.length) {
		payload = enrichFilesFromItems(payload, items);
	}

	return { payload, items };
};

const enrichFilesFromItems = (payload, items = []) => {
	const next = { ...payload };
	const contractItems = items.filter(
		(item) => item.entityType === 'contracts',
	);
	const documentItems = items.filter(
		(item) => item.entityType === 'documents',
	);

	if (next.contracts?.length) {
		next.contracts = next.contracts.map((contract, index) => {
			const item = contractItems[index];
			const fileId =
				contract.fileId ??
				item?.newValues?.fileId ??
				contract.file?.id ??
				null;

			if (!fileId) return contract;

			return {
				...contract,
				fileId,
				file: {
					...(contract.file ?? {}),
					id: String(fileId),
					name: contract.file?.name ?? contract.name,
				},
			};
		});
	}

	if (next.documents?.length) {
		next.documents = next.documents.map((document, index) => {
			const item = documentItems[index];
			const fileId =
				document.fileId ??
				item?.newValues?.fileId ??
				document.file?.id ??
				null;

			if (!fileId) return document;

			return {
				...document,
				fileId,
				file: {
					...(document.file ?? {}),
					id: String(fileId),
					name: document.file?.name ?? document.name,
				},
			};
		});
	}

	return next;
};

const buildPayloadFromItems = (items = []) => {
	const payload = {};

	for (const item of items) {
		const data = {
			...(item.payloadJson ?? {}),
			...(item.newValues ?? {}),
		};

		switch (item.entityType) {
			case 'objections':
				payload.objections = [...(payload.objections ?? []), data];
				break;
			case 'personal_contacts':
				payload.personalContacts = [
					...(payload.personalContacts ?? []),
					data,
				];
				break;
			case 'company_general_numbers':
				payload.generalNumbers = [
					...(payload.generalNumbers ?? []),
					data,
				];
				break;
			case 'contracts':
				payload.contracts = [...(payload.contracts ?? []), data];
				break;
			case 'documents':
				payload.documents = [...(payload.documents ?? []), data];
				break;
			case 'company_params':
				payload.companyParams = data;
				break;
			case 'companies':
				payload.companyStatusId = data.companyStatusId ?? data.statusId;
				payload.statusPostponedUntil =
					data.statusPostponedUntil ?? data.postponedUntil;
				payload.statusPostponedReason =
					data.statusPostponedReason ?? data.postponedReason;
				payload.statusLossReason =
					data.statusLossReason ?? data.lossReason;
				break;
			case 'meetings':
				payload.meetings = [...(payload.meetings ?? []), data];
				break;
			case 'messaging':
				payload.messagings = [...(payload.messagings ?? []), data];
				break;
			case 'calls':
				payload.calls = {
					attach: [
						...(payload.calls?.attach ?? []),
						...(data.callId ? [data.callId] : []),
					],
				};
				break;
			case 'worths':
				payload.worth = data;
				break;
			case 'company_group_links': {
				payload.companyGroupLinks = payload.companyGroupLinks || {};

				if (item.operationType === 'link') {
					const linkIds = Array.isArray(item.payloadJson)
						? item.payloadJson
						: item.newValues?.link;

					if (linkIds?.length) {
						payload.companyGroupLinks.link = linkIds;
					}
				}

				if (item.operationType === 'unlink') {
					const unlinkIds = Array.isArray(item.payloadJson)
						? item.payloadJson
						: item.newValues?.unlink;

					if (unlinkIds?.length) {
						payload.companyGroupLinks.unlink = unlinkIds;
					}
				}

				break;
			}
			default:
				break;
		}
	}

	return payload;
};

const mapAreas = (ids = [], context = {}) =>
	ids
		.map((id) => String(id))
		.filter(Boolean)
		.map((id) => ({
			value: id,
			label: getCompanyAreaLabel(context.companyById, id),
		}));

const mapFileForForm = (file, fallbackName, fileId) => {
	const id = file?.id ?? fileId;
	const name = file?.name ?? fallbackName;

	if (!name && !id) return [];

	return [
		{
			...(id ? { id: String(id) } : {}),
			name: name || 'Файл',
		},
	];
};

/**
 * Maps an activity (with lastVersion) into EventForm initial state.
 */
export const mapActivityToEventFormState = (activity, context = {}) => {
	if (!activity) return null;

	const { payload, items } = resolveActivityPayload(activity);
	const payloadItems = [];
	const paramsItem = items.find(
		(item) => item.entityType === 'company_params',
	);
	const paramsOldValues = paramsItem?.oldValues ?? {};

	const statusId = payload.companyStatusId;
	const status =
		statusId != null
			? (context.statusOptions || []).find(
				(option) => String(option.value) === String(statusId),
			) || {
				label:
						context.statusById?.[statusId]?.name ??
						String(statusId),
				value: statusId,
			}
			: NO_CHANGE_STATUS_OPTION;

	const statusReasonValue =
		payload.statusLossReason || payload.statusPostponedReason;
	const statusReason = statusReasonValue
		? STATUS_REASON_OPTIONS.find(
			(option) =>
				String(option.value).toLowerCase() ===
				String(statusReasonValue).toLowerCase(),
		) || {
			label: 'Не удалось связаться',
			value: 'NO_CONTACT',
		}
		: null;

	const statusDate = payload.statusPostponedUntil || null;

	for (const objection of payload.objections ?? []) {
		const isRemoved = objection.solved === true && Boolean(objection.id);
		const typeId = objection.typeId;
		const label = getObjectionTypeName(context.objectionTypeById, typeId);

		if (isRemoved) {
			payloadItems.push({
				type: 'objection',
				action: 'remove',
				data: {
					objection: {
						id: objection.id,
						typeId,
						comment: objection.comment || '',
						label,
						remark: objection.comment || '',
					},
				},
			});
		} else {
			payloadItems.push({
				type: 'objection',
				action: 'create',
				data: {
					objection: {
						id: typeId,
						label,
					},
					comment: objection.comment || '',
				},
			});
		}
	}

	for (const contact of payload.personalContacts ?? []) {
		const formData = {
			name: contact.firstName || '',
			secondName: contact.lastName || '',
			thirdName: contact.middleName || '',
			contacts: mapContactNumbers(contact.numbers),
			job: findJobOption(contact.position),
			decisionMaker: contact.responsiblePerson ? '1' : '0',
			comment: contact.comment || '',
		};

		if (contact.id) {
			const target = {
				id: contact.id,
				value: String(contact.id),
				isPerson: true,
				firstName: contact.firstName,
				lastName: contact.lastName,
				middleName: contact.middleName,
				position: contact.position,
				responsiblePerson: contact.responsiblePerson,
				active: contact.active !== false,
				comment: contact.comment,
				numbers: contact.numbers,
			};

			payloadItems.push({
				type: 'contact',
				action: 'change',
				data: {
					...formData,
					target,
					status: {
						label:
							contact.active === false
								? 'Больше не работает'
								: 'Работает',
						value: contact.active === false ? 'unavail' : 'avail',
					},
				},
			});
		} else {
			payloadItems.push({
				type: 'contact',
				action: 'create',
				data: formData,
			});
		}
	}

	for (const number of payload.generalNumbers ?? []) {
		if (number.id) {
			const target = {
				id: number.id,
				value: String(number.id),
				isPerson: false,
				number: number.number,
				label: number.number,
				comment: number.comment,
				active: number.active !== false,
			};

			payloadItems.push({
				type: 'contact',
				action: 'change',
				data: {
					target,
					phone: number.number || '',
					comment: number.comment || '',
					status: {
						label:
							number.active === false
								? 'Больше не работает'
								: 'Работает',
						value: number.active === false ? 'unavail' : 'avail',
					},
				},
			});
		} else {
			payloadItems.push({
				type: 'number',
				action: 'create',
				data: {
					phone: number.number || '',
					comment: number.comment || '',
				},
			});
		}
	}

	for (const contract of payload.contracts ?? []) {
		payloadItems.push({
			type: 'contract',
			action: 'create',
			data: {
				title: contract.name || '',
				signed: contract.signed ? '1' : '0',
				files: mapFileForForm(
					contract.file,
					contract.name,
					contract.fileId,
				),
			},
		});
	}

	for (const document of payload.documents ?? []) {
		payloadItems.push({
			type: 'doc',
			action: 'create',
			data: {
				title: document.name || '',
				files: mapFileForForm(
					document.file,
					document.name,
					document.fileId,
				),
			},
		});
	}

	if (payload.companyParams) {
		const params = payload.companyParams;

		payloadItems.push({
			type: 'params',
			action: 'change',
			data: {
				date: params.knownFrom
					? dayjs(params.knownFrom)
					: params.openingDate
						? dayjs(params.openingDate)
						: null,
				emails: (params.email ?? []).filter(Boolean),
				sites: (
					params.sitesExternal ??
					params.site ??
					[]
				).filter(Boolean),
			},
			initialData: {
				knownFrom:
					paramsOldValues.knownFrom ??
					paramsOldValues.openingDate ??
					null,
				emails: (paramsOldValues.email ?? []).filter(Boolean),
				sites: (
					paramsOldValues.sitesExternal ??
					paramsOldValues.site ??
					[]
				).filter(Boolean),
			},
		});
	}

	if (payload.companyGroupLinks?.link?.length) {
		payloadItems.push({
			type: 'companyGroup',
			action: 'link',
			data: {
				areas: mapAreas(payload.companyGroupLinks.link, context),
			},
		});
	}

	if (payload.companyGroupLinks?.unlink?.length) {
		payloadItems.push({
			type: 'companyGroup',
			action: 'unlink',
			data: {
				areas: mapAreas(payload.companyGroupLinks.unlink, context),
			},
		});
	}

	for (const meeting of payload.meetings ?? []) {
		const date = meeting.date ? dayjs(meeting.date) : null;
		const time =
			meeting.date && meeting.time
				? dayjs(`${meeting.date}T${meeting.time}`)
				: null;

		payloadItems.push({
			type: 'meeting',
			action: 'create',
			data: {
				date,
				time,
				place: meeting.place || '',
			},
		});
	}

	for (const messaging of payload.messagings ?? []) {
		payloadItems.push({
			type: 'messaging',
			action: 'create',
			data: {
				date: messaging.date ? dayjs(messaging.date) : null,
			},
		});
	}

	if (payload.worth) {
		payloadItems.push({
			type: 'worth',
			action: 'change',
			data: mapWorthToFormValues(payload.worth),
		});
	}

	return {
		activityId: activity.id,
		comment: activity.comment || '',
		status,
		statusReason,
		statusDate,
		payloadItems: payloadItems.map((item) => ({
			...item,
			id: crypto.randomUUID(),
		})),
	};
};
